"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useReferralCode } from "@/components/referral-code-capture";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingIcon,
  CheckCircle2Icon,
  EyeIcon,
  EyeOffIcon,
  GlobeIcon,
  Loader2Icon,
  LockIcon,
  MailIcon,
  PartyPopperIcon,
  RocketIcon,
  SparklesIcon,
} from "lucide-react";
import { signIn } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

// ─── Schema ──────────────────────────────────────────────────────────
const step1Schema = z.object({
  businessName: z
    .string()
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(120),
  slug: z
    .string()
    .min(2, "Endereço deve ter pelo menos 2 caracteres")
    .max(60)
    .regex(
      /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
      "Apenas letras minúsculas, números e hífens"
    ),
});

const step2Schema = z
  .object({
    email: z.string().email("E-mail inválido"),
    password: z.string().min(8, "Mínimo de 8 caracteres"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Senhas não conferem",
    path: ["confirmPassword"],
  });

type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;

// ─── Slug helpers ────────────────────────────────────────────────────
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .replace(/[^a-z0-9]+/g, "-") // non-alphanumeric → hyphen
    .replace(/^-+|-+$/g, "") // trim hyphens
    .slice(0, 60);
}

// ─── Component ───────────────────────────────────────────────────────
export default function CadastroPage() {
  const router = useRouter();
  const referralCode = useReferralCode();
  const [step, setStep] = useState(1);
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [, startTransition] = useTransition();
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 1 form
  const form1 = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: { businessName: "", slug: "" },
  });

  // Step 2 form
  const form2 = useForm<Step2Data>({
    resolver: zodResolver(step2Schema),
    defaultValues: { email: "", password: "", confirmPassword: "" },
  });

  const currentSlug = form1.watch("slug");

  // Auto-generate slug from business name
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value;
      form1.setValue("businessName", name);
      const slug = toSlug(name);
      form1.setValue("slug", slug, { shouldValidate: slug.length >= 2 });
    },
    [form1]
  );

  // Debounced slug availability check
  useEffect(() => {
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    setSlugAvailable(null);

    if (!currentSlug || currentSlug.length < 2) return;

    // Validate format first
    const isValidFormat = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(currentSlug);
    if (!isValidFormat) return;

    setSlugChecking(true);
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/v1/register/check-slug?slug=${encodeURIComponent(currentSlug)}`
        );
        const json = await res.json();
        if (json.success) {
          setSlugAvailable(json.data.available);
        }
      } catch {
        // fail silently
      } finally {
        setSlugChecking(false);
      }
    }, 500);

    return () => {
      if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    };
  }, [currentSlug]);

  // Step 1 → Step 2
  const onStep1Submit = form1.handleSubmit((data) => {
    if (slugAvailable === false) {
      form1.setError("slug", { message: "Este endereço já está em uso" });
      return;
    }
    setStep1Data(data);
    setStep(2);
  });

  // Step 2 → Submit + Auto-login
  const onStep2Submit = form2.handleSubmit(async (data) => {
    if (!step1Data) return;
    setSubmitting(true);

    try {
      const res = await fetch("/api/v1/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessName: step1Data.businessName,
          slug: step1Data.slug,
          email: data.email,
          password: data.password,
          referralCode: referralCode || undefined,
        }),
      });

      const json = await res.json();

      if (!res.ok) {
        const msg = json.message || json.error || "Erro ao criar conta";
        toast.error(msg);
        // If email taken, stay on step 2
        if (res.status === 409 && msg.includes("e-mail")) {
          form2.setError("email", { message: msg });
        }
        // If slug taken, go back to step 1
        if (res.status === 409 && msg.includes("endereço")) {
          setStep(1);
          form1.setError("slug", { message: msg });
        }
        setSubmitting(false);
        return;
      }

      // Success! Show step 3 then auto-login
      setStep(3);

      // Auto-login
      const loginResult = await signIn("credentials", {
        redirect: false,
        email: data.email,
        password: data.password,
      });

      if (loginResult?.error) {
        // Login failed but account was created - redirect to login
        toast.error("Conta criada! Faça login para continuar.");
        setTimeout(() => router.push("/login"), 2000);
        return;
      }

      // Redirect to dashboard after a celebratory delay
      startTransition(() => {
        setTimeout(() => router.push("/login/redirect"), 2500);
      });
    } catch {
      toast.error("Erro de conexão. Tente novamente.");
      setSubmitting(false);
    }
  });

  return (
    <div className="min-h-dvh flex items-center justify-center p-4 bg-[#050505] text-white selection:bg-primary selection:text-white relative overflow-hidden dark">
      {/* Background */}
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-primary/20 blur-[180px] rounded-full mix-blend-screen pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-600/15 blur-[150px] rounded-full mix-blend-screen pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_10%,transparent_100%)] pointer-events-none" />

      <div className="w-full max-w-lg relative z-10">
        {/* Back Link */}
        <Link
          href={step === 2 ? "#" : "/"}
          onClick={(e) => {
            if (step === 2) {
              e.preventDefault();
              setStep(1);
            }
          }}
          className="inline-flex items-center gap-2 mb-6 text-white/50 hover:text-white transition-colors text-sm font-medium group"
        >
          <ArrowLeftIcon className="size-4 group-hover:-translate-x-0.5 transition-transform" />
          {step === 2 ? "Voltar" : "Voltar para o site"}
        </Link>

        {/* Progress Indicator */}
        {step < 3 && (
          <div className="flex items-center gap-3 mb-8">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center gap-3 flex-1">
                <div
                  className={`
                    size-8 rounded-full flex items-center justify-center text-sm font-bold border transition-all duration-500
                    ${
                      step >= s
                        ? "bg-white text-black border-white shadow-[0_0_20px_rgba(255,255,255,0.3)]"
                        : "bg-white/5 text-white/40 border-white/10"
                    }
                  `}
                >
                  {step > s ? (
                    <CheckCircle2Icon className="size-4" />
                  ) : (
                    s
                  )}
                </div>
                {s < 2 && (
                  <div className="flex-1 h-px relative overflow-hidden">
                    <div className="absolute inset-0 bg-white/10" />
                    <div
                      className="absolute inset-y-0 left-0 bg-white transition-all duration-700 ease-out"
                      style={{ width: step > 1 ? "100%" : "0%" }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Card */}
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-purple-500/30 rounded-3xl blur-xl opacity-50 transition duration-1000" />

          <div className="relative bg-[#0a0a0a]/90 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden">
            {/* Ambient glow inside card */}
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/5 blur-[100px] rounded-full pointer-events-none translate-x-1/2 -translate-y-1/2" />

            <div className="relative z-10">
              {/* ─── Step 1: Negócio ──────────────────────── */}
              {step === 1 && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                  <div className="flex flex-col items-center text-center gap-3 mb-8">
                    <div className="size-14 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-white/10 flex items-center justify-center mb-1">
                      <BuildingIcon className="size-6 text-primary" />
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                      Crie sua conta grátis
                    </h1>
                    <p className="text-white/50 text-sm max-w-sm">
                      Comece seus{" "}
                      <span className="text-primary font-semibold">
                        30 dias gratuitos
                      </span>{" "}
                      em menos de 1 minuto. Sem cartão de crédito.
                    </p>
                  </div>

                  <form onSubmit={onStep1Submit} className="space-y-5">
                    {/* Business Name */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                        Nome do seu negócio
                      </label>
                      <div className="relative">
                        <BuildingIcon className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-white/30" />
                        <Input
                          placeholder="Ex: Studio Beleza Maria"
                          autoFocus
                          autoComplete="organization"
                          className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-primary rounded-xl"
                          {...form1.register("businessName")}
                          onChange={handleNameChange}
                        />
                      </div>
                      {form1.formState.errors.businessName && (
                        <p className="text-xs text-red-400 font-medium">
                          {form1.formState.errors.businessName.message}
                        </p>
                      )}
                    </div>

                    {/* Slug */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                        Endereço da sua página
                      </label>
                      <div className="relative">
                        <GlobeIcon className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-white/30" />
                        <Input
                          placeholder="seu-negocio"
                          autoComplete="off"
                          className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-primary rounded-xl font-mono text-sm"
                          {...form1.register("slug")}
                        />
                        {/* Availability indicator */}
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          {slugChecking && (
                            <Loader2Icon className="size-4 text-white/40 animate-spin" />
                          )}
                          {!slugChecking && slugAvailable === true && (
                            <CheckCircle2Icon className="size-4 text-green-400" />
                          )}
                          {!slugChecking && slugAvailable === false && (
                            <span className="text-xs text-red-400 font-medium">
                              Em uso
                            </span>
                          )}
                        </div>
                      </div>
                      {form1.formState.errors.slug && (
                        <p className="text-xs text-red-400 font-medium">
                          {form1.formState.errors.slug.message}
                        </p>
                      )}
                      {/* URL Preview */}
                      {currentSlug && currentSlug.length >= 2 && (
                        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/5">
                          <GlobeIcon className="size-3.5 text-primary/60 shrink-0" />
                          <p className="text-xs text-white/40 truncate">
                            <span className="text-primary font-medium">
                              {currentSlug}
                            </span>
                            .agendei.kayooliveira.com
                          </p>
                        </div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={slugAvailable === false || slugChecking}
                      className="w-full h-12 rounded-xl text-base font-bold bg-white text-black hover:bg-white/90 shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all group mt-2"
                    >
                      Continuar
                      <ArrowRightIcon className="size-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  </form>

                  <p className="text-center text-[11px] text-white/30 mt-6">
                    Já tem uma conta?{" "}
                    <Link
                      href="/login"
                      className="text-primary hover:text-primary/80 font-medium transition-colors"
                    >
                      Fazer login
                    </Link>
                  </p>
                </div>
              )}

              {/* ─── Step 2: Credenciais ──────────────────── */}
              {step === 2 && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                  <div className="flex flex-col items-center text-center gap-3 mb-8">
                    <div className="size-14 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-white/10 flex items-center justify-center mb-1">
                      <LockIcon className="size-6 text-primary" />
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                      Dados de acesso
                    </h1>
                    <p className="text-white/50 text-sm">
                      Quase lá! Defina seu e-mail e senha para acessar o painel.
                    </p>
                  </div>

                  {/* Mini preview of business */}
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/5 mb-6">
                    <div className="size-9 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                      <BuildingIcon className="size-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {step1Data?.businessName}
                      </p>
                      <p className="text-[11px] text-white/40 truncate">
                        {step1Data?.slug}.agendei.kayooliveira.com
                      </p>
                    </div>
                  </div>

                  <form onSubmit={onStep2Submit} className="space-y-5">
                    {/* Email */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                        Seu e-mail
                      </label>
                      <div className="relative">
                        <MailIcon className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-white/30" />
                        <Input
                          type="email"
                          placeholder="seu@email.com"
                          autoFocus
                          autoComplete="email"
                          className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-primary rounded-xl"
                          {...form2.register("email")}
                        />
                      </div>
                      {form2.formState.errors.email && (
                        <p className="text-xs text-red-400 font-medium">
                          {form2.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Password */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                        Crie uma senha
                      </label>
                      <div className="relative">
                        <LockIcon className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-white/30" />
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Mínimo 8 caracteres"
                          autoComplete="new-password"
                          className="h-12 pl-11 pr-11 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-primary rounded-xl"
                          {...form2.register("password")}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOffIcon className="size-4" />
                          ) : (
                            <EyeIcon className="size-4" />
                          )}
                        </button>
                      </div>
                      {form2.formState.errors.password && (
                        <p className="text-xs text-red-400 font-medium">
                          {form2.formState.errors.password.message}
                        </p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                        Confirme a senha
                      </label>
                      <div className="relative">
                        <LockIcon className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-white/30" />
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Repita a senha"
                          autoComplete="new-password"
                          className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-primary rounded-xl"
                          {...form2.register("confirmPassword")}
                        />
                      </div>
                      {form2.formState.errors.confirmPassword && (
                        <p className="text-xs text-red-400 font-medium">
                          {form2.formState.errors.confirmPassword.message}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-12 rounded-xl text-base font-bold bg-white text-black hover:bg-white/90 shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all group mt-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2Icon className="size-4 mr-2 animate-spin" />
                          Criando sua conta...
                        </>
                      ) : (
                        <>
                          <RocketIcon className="size-4 mr-2" />
                          Criar minha conta grátis
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-center text-[11px] text-white/30 mt-6">
                    Ao criar sua conta, você concorda com nossos{" "}
                    <Link
                      href="/termos"
                      className="text-white/50 hover:text-white/70 underline underline-offset-2 transition-colors"
                    >
                      Termos de Uso
                    </Link>{" "}
                    e{" "}
                    <Link
                      href="/privacidade"
                      className="text-white/50 hover:text-white/70 underline underline-offset-2 transition-colors"
                    >
                      Política de Privacidade
                    </Link>
                    .
                  </p>
                </div>
              )}

              {/* ─── Step 3: Sucesso ──────────────────────── */}
              {step === 3 && (
                <div className="animate-in fade-in zoom-in-95 duration-700 flex flex-col items-center text-center gap-5 py-6">
                  {/* Celebration icon */}
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/30 blur-[40px] rounded-full animate-pulse" />
                    <div className="relative size-20 rounded-3xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center shadow-[0_0_40px_rgba(var(--primary),0.4)]">
                      <PartyPopperIcon className="size-9 text-white" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                      Conta criada com sucesso!
                    </h1>
                    <p className="text-white/50 text-sm max-w-sm mx-auto">
                      Seu período de teste de{" "}
                      <span className="text-primary font-semibold">
                        30 dias gratuitos
                      </span>{" "}
                      já começou. Estamos te levando para o painel...
                    </p>
                  </div>

                  {/* Success details */}
                  <div className="w-full space-y-3 mt-2">
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/20">
                      <CheckCircle2Icon className="size-5 text-green-400 shrink-0" />
                      <p className="text-sm text-green-300 text-left">
                        Página de agendamento ativa em{" "}
                        <span className="font-semibold">
                          {step1Data?.slug}.agendei.kayooliveira.com
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 border border-primary/20">
                      <SparklesIcon className="size-5 text-primary shrink-0" />
                      <p className="text-sm text-primary/80 text-left">
                        Configure seus serviços e horários no painel para
                        começar a receber agendamentos
                      </p>
                    </div>
                  </div>

                  {/* Loading redirect */}
                  <div className="flex items-center gap-2 mt-4 text-white/40">
                    <Loader2Icon className="size-4 animate-spin" />
                    <span className="text-sm">
                      Redirecionando para o painel...
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trust indicators */}
        {step < 3 && (
          <div className="flex items-center justify-center gap-6 mt-8 text-[11px] text-white/30">
            {["Sem cartão de crédito", "Cancele quando quiser", "Suporte humanizado"].map(
              (t) => (
                <div key={t} className="flex items-center gap-1.5">
                  <CheckCircle2Icon className="size-3 text-white/20" />
                  <span>{t}</span>
                </div>
              )
            )}
          </div>
        )}

        {/* Logo */}
        <div className="flex justify-center mt-8 opacity-30">
          <Image src="/logo-white.png" alt="Agendei" width={20} height={20} />
        </div>
      </div>
    </div>
  );
}
